import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import FormControlLabel from "@mui/material/FormControlLabel";
import classNames from "classnames";
import { sanitize } from "isomorphic-dompurify";
import { Controller, useFormContext } from "../../components/Form";
import { Info } from "../../components/Info/Info";
import { MarkdownWrapper } from "../../components/MarkdownWrapper";
import { Radio, RadioGroup } from "../../components/Radio/Radio";
import { useConfig } from "../../providers/useConfig";
import { useTranslations } from "../../providers/useTranslations";
import { replaceOneTrustPlaceholdersWithLinks } from "../../utils/linkReplacer/linkReplacer";
import styles from "./ConsentSection.module.scss";
const MULTIOPTION_SEPARATOR = "--";
const DEFAULT_TRANSLATIONS_PREFIX = "profile.communication.manage_consent";
const DEFAULT_OPTIONS_LIST = ["yes", "no"];
const EXTENDED_OPTIONS_LIST = ["yes.until", "yes.one_off", "no.one_off"];
/**
 * Maps consent option keys to a combined string value.
 *
 * Radio groups can only hold one value. To work around this, we combine the
 * "accepted" and "one_off" properties into a single string.  This string is
 * then used as the radio group's value.
 *
 * The string format is "accepted--one_off".
 *   - accepted:  "true" or "false"
 *   - one_off: "true" or "false"
 *
 * Example: "true--false" means accepted=true, one_off=false.
 *
 * NOTE: Before sending to the API, this combined string is split back into
 * its separate "accepted" and "one_off" values. See:
 * frontend-libs/packages/user-auth/src/pages/Registration/NewRegistrationForm/utils/transformToCasStructure/transformToCasConsents.ts
 * function transformToCasConsents
 */
const optionsMapper = {
    yes: "true",
    no: "false",
    "yes.until": "true--false",
    "yes.one_off": "true--true",
    "no.one_off": "false--false",
};
export const PurposeMarkdownText = ({ text, className }) => {
    const { ONE_TRUST_DYNAMIC_LINKS: otdl } = useConfig();
    return (_jsx("div", { className: className, children: _jsx(MarkdownWrapper, { children: sanitize(otdl ? replaceOneTrustPlaceholdersWithLinks(text, otdl) : text, { ADD_ATTR: ["target"] }) }) }));
};
export const mapSectionWithExtendedOptions = ({ sections, resetDefaultValues = false, mapToExtendedDefaultValue = false, }) => sections.map((section) => {
    // Double option list only for the "Ploom Club" section
    const isPloomClubSection = section.purposeTitle?.trim().toLowerCase().includes("ploom club");
    const optionsList = isPloomClubSection ? undefined : EXTENDED_OPTIONS_LIST;
    return {
        ...section,
        optionsList,
        purposes: section.purposes.map((purpose) => ({
            ...purpose,
            ...(resetDefaultValues && { default: null }),
            ...(mapToExtendedDefaultValue && {
                default: isPloomClubSection
                    ? `${purpose.accepted}`
                    : `${purpose.accepted}${MULTIOPTION_SEPARATOR}${purpose.one_off}`,
            }),
        })),
    };
});
export const ConsentSection = ({ section, className, translationsPrefix = DEFAULT_TRANSLATIONS_PREFIX, formPrefix = "purposes", }) => {
    const { control, formState: { errors }, } = useFormContext();
    const { __ } = useTranslations();
    const titleOrDescription = section.purposeTitle || section.purposeDescription;
    return (_jsxs("div", { className: classNames(styles.sectionContainer, className), "data-testid": section.purposeId, children: [titleOrDescription && (_jsxs("div", { children: [section.purposeTitle && _jsx("h2", { className: styles.sectionTitle, children: section.purposeTitle }), section.purposeDescription && (_jsx(PurposeMarkdownText, { text: section.purposeDescription, className: styles.sectionDescription }))] })), section.purposes.map(({ id, name, description, default: defaultValue }) => {
                const safeDefaultValue = defaultValue ?? "";
                const errorMessage = errors?.purposes?.[id]?.message;
                const optionsList = section.optionsList || DEFAULT_OPTIONS_LIST;
                return (_jsxs("div", { className: styles.purposeGroup, children: [_jsx(PurposeMarkdownText, { text: name, className: styles.purposeTitle }, `${id}-name`), _jsx(PurposeMarkdownText, { text: description, className: styles.purposeSubtitle }, `${id}-description`), _jsx(Controller, { rules: {
                                required: __("form_validation.field_required"),
                            }, name: `${formPrefix}.${id}`, control: control, defaultValue: safeDefaultValue.toString(), render: ({ field }) => (_jsx(RadioGroup, { ...field, "aria-label": `consent-${name}`, className: styles.radioGroup, children: optionsList.map((option) => (_jsx(FormControlLabel, { label: __(`${translationsPrefix}.${option}`), value: optionsMapper[option], control: _jsx(Radio, { "data-testid": `radio-${option}-${id}` }) }, option))) })) }, id), errorMessage && _jsx(Info, { variant: "error", message: errorMessage })] }, id));
            })] }));
};
//# sourceMappingURL=ConsentSection.js.map